import { CatchError } from 'Exceptions/exceptions'
import { DEFAULT_GLOBAL_CALLBACK_FILTERS } from 'Main/Constant'
import { DEFAULT_GLOBAL_CALLBACK_TYPES } from 'Main/Constant'
import GlobalCallbackLogsService from 'Main/service/GlobalCallbackLogsService'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getRefreshToken } from 'variables/common'
import GlobalCallbackLogsToolbar from 'views/components/Bar/GlobalCallbackLogsToolbar'
import DatalogPagination from 'views/components/Datalog/DatalogPagination'
import GlobalCallbackLogsTable from 'views/components/Table/GlobalCallbackLogsTable'
import useLogUserFilter from 'views/dashboard/logs/useLogUserFilter'

function GlobalCallbackLogsManager () {
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const globalCallbackLogsService = useMemo(() => new GlobalCallbackLogsService(), [])
  const { isSuperAdmin, users, loadingUsers } = useLogUserFilter()

  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [currentPage, setPage] = useState(1)
  const [totalPage, setTotalPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)

  const [filters, setFilters] = useState(DEFAULT_GLOBAL_CALLBACK_FILTERS)
  const [options, setOptions] = useState({ types: DEFAULT_GLOBAL_CALLBACK_TYPES, vendors: [], configs: [], endpoints: [] })

  const fetchLogs = useCallback(
    async (page = 1, activeFilters = DEFAULT_GLOBAL_CALLBACK_FILTERS) => {
      try {
        setLoading(true)
        setError(null)

        const res = await globalCallbackLogsService.getData({
          page,
          size: activeFilters.pageSize,
          endpoint: activeFilters.endpoint,
          status: activeFilters.status,
          type: activeFilters.type,
          paymentVendorsId: activeFilters.paymentVendorsId,
          qrisExtConfigsId: activeFilters.qrisExtConfigsId,
          from: activeFilters.from,
          to: activeFilters.to,
          userId: activeFilters.userId
        })
        const data = res.data

        setLogs(data?.result || [])
        setTotalPage(data?.totalPage || 1)
        setTotalRecords(data?.max || 0)

        // Fall back to deriving endpoint options from the current page if the
        // dedicated filters lookup below hasn't populated them yet.
        setOptions((prev) => {
          if (prev.endpoints.length) return prev
          const derived = Array.from(
            new Set((data?.result || []).map((el) => el.callback_url).filter(Boolean))
          ).sort()
          return { ...prev, endpoints: derived }
        })
      } catch (err) {
        const caught = CatchError(err, navigate, refreshToken)
        if (caught !== undefined) setError('Failed to load global callback logs.')
      } finally {
        setLoading(false)
      }
    },
    [globalCallbackLogsService, navigate, refreshToken]
  )

  const loadFilterOptions = useCallback((userId = 'ALL') => {
    globalCallbackLogsService
      .getFilters(userId)
      .then((res) => {
        const data = res.data || {}
        setOptions({
          types: Array.isArray(data.types) && data.types.length ? data.types : DEFAULT_GLOBAL_CALLBACK_TYPES,
          vendors: Array.isArray(data.vendors) ? data.vendors : [],
          configs: Array.isArray(data.configs) ? data.configs : [],
          endpoints: Array.isArray(data.endpoints) ? data.endpoints : []
        })
      })
      .catch(() => {
        setOptions({ types: DEFAULT_GLOBAL_CALLBACK_TYPES, vendors: [], configs: [], endpoints: [] })
      })
  }, [globalCallbackLogsService])

  useEffect(() => {
    fetchLogs(1, DEFAULT_GLOBAL_CALLBACK_FILTERS)
    loadFilterOptions('ALL')
  }, [fetchLogs, loadFilterOptions])

  const handleFieldChange = (field, value) => {
    const nextFilters = field === 'userId'
      ? { ...DEFAULT_GLOBAL_CALLBACK_FILTERS, pageSize: filters.pageSize, userId: value }
      : { ...filters, [field]: value }
    setFilters(nextFilters)
    setPage(1)
    fetchLogs(1, nextFilters)
    if (field === 'userId') loadFilterOptions(value)
  }

  const handleClear = () => {
    setFilters(DEFAULT_GLOBAL_CALLBACK_FILTERS)
    setPage(1)
    fetchLogs(1, DEFAULT_GLOBAL_CALLBACK_FILTERS)
    loadFilterOptions('ALL')
  }

  const handlePageChange = (page) => {
    setPage(page)
    fetchLogs(page, filters)
  }

  const handleView = (id) => {
    navigate(`/dashboard/global-callback-logs/${id}`)
  }

  return (
    <div className="page-content">
      <section>
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <i className="fa-solid fa-satellite-dish me-1" /> Callback Logs
            </h5>
            <span className="text-white">
              Total <strong>{totalRecords || 0}</strong> logs
            </span>
          </div>

          <div className="card-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                <i className="fa fa-exclamation-circle me-1" />
                {error}
              </div>
            )}

            <GlobalCallbackLogsToolbar
              filters={filters}
              options={options}
              showUserFilter={isSuperAdmin}
              users={users}
              loadingUsers={loadingUsers}
              onFieldChange={handleFieldChange}
              onClear={handleClear}
            />

            <GlobalCallbackLogsTable logs={logs} loading={loading} onView={handleView} />

            <div className="mt-3">
              <DatalogPagination
                currentPage={currentPage}
                totalPage={totalPage}
                totalRecords={totalRecords}
                pageSize={filters.pageSize}
                loading={loading}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default GlobalCallbackLogsManager
